<template>
  <el-form
    class="contact-person-form"
    ref="form"
    :model="model"
    :rules="rules"
  >
    <!-- Uncollapsed state -->
    <template v-if="!isCollapsed">
      <el-row :gutter="gutter">
        <el-col
          class="contact-person-form__header"
          :lg="24" :md="24" :sm="24" :xs="24"
        >
          <app-form-subtitle
            class="contact-person-form__subtitle"
            i18nCode="teamRegistrationSite.form.step3.subtitles.personInCharge"
            :suffix="String(index + 1)"
          />
          <div class="contact-person-form__controls">
            <i
              class="contact-person-form__delete icon-quit"
              @click.prevent.stop="showConfirmationAlert"
            />
          </div>
        </el-col>

        <el-col :lg="12" :md="12" :sm="12" :xs="24">
          <el-form-item
            :label="fields.firstName.label"
            :prop="fields.firstName.name"
            :required="fields.firstName.required"
          >
            <el-input
              v-model="model.firstName"
              :name="fields.firstName.name"
              prefix-icon="icon-player"
              :placeholder="i18n('entities.clubContactPerson.placeholders.firstName')"
            />
          </el-form-item>
        </el-col>

        <el-col :lg="12" :md="12" :sm="12" :xs="24">
          <el-form-item
            :label="fields.lastName.label"
            :prop="fields.lastName.name"
            :required="fields.lastName.required"
          >
            <el-input
              v-model="model.lastName"
              :name="fields.lastName.name"
              prefix-icon="icon-player"
              :placeholder="i18n('entities.clubContactPerson.placeholders.lastName')"
            />
          </el-form-item>
        </el-col>

        <el-col :lg="12" :md="12" :sm="12" :xs="24">
          <el-form-item
            :label="fields.position.label"
            :prop="fields.position.name"
            :required="fields.position.required"
          >
            <el-input
              v-model="model.position"
              :name="fields.position.name"
              prefix-icon="icon-suitcase"
              :placeholder="i18n('entities.clubContactPerson.placeholders.position')"
            />
          </el-form-item>
        </el-col>

        <el-col :lg="12" :md="12" :sm="12" :xs="24">
          <el-form-item
            :label="fields.email.label"
            :prop="fields.email.name"
            :required="fields.email.required"
          >
            <el-input
              v-model="model.email"
              :name="fields.email.name"
              prefix-icon="icon-mail"
              :placeholder="i18n('entities.clubContactPerson.placeholders.email')"
            />
          </el-form-item>
        </el-col>

        <el-col :lg="12" :md="12" :sm="12" :xs="24">
          <el-form-item
            :label="fields.phone.label"
            :prop="fields.phone.name"
            :required="fields.phone.required"
          >
            <app-phone-input v-model="model.phone" />
          </el-form-item>
        </el-col>

        <el-col
          class="contact-person-form__save"
          :lg="12" :md="12" :sm="12" :xs="24"
        >
          <el-button
            class="contact-person-form__save__button"
            type="primary"
            @click="() => validateAndCollapse()"
          >
            <app-i18n code="common.save" />
          </el-button>
        </el-col>
      </el-row>
    </template>

    <!-- Collapsed state -->
    <template v-else>
      <el-row :gutter="gutter * 4">
        <el-col
          class="contact-person-form__header"
          :lg="24" :md="24" :sm="24" :xs="24"
        >
          <app-form-subtitle
            class="contact-person-form__subtitle"
            i18n-code="teamRegistrationSite.form.step3.subtitles.personInCharge"
            :suffix="String(index + 1)"
          />
          <div class="contact-person-form__overview">
            <p v-text="`${model.firstName} ${model.lastName} - ${model.position}`" />
            <p v-text="`${model.email} - ${model.phone.prefix} ${model.phone.number}`" />
          </div>
          <div class="contact-person-form__controls">
            <span
              class="contact-person-form__edit"
              @click.prevent.stop="uncollapse"
            >
              <i class="contact-person-form__edit__icon icon-pen" />
            </span>
            <i
              class="contact-person-form__delete icon-quit"
              @click.prevent.stop="showConfirmationAlert"
            />
          </div>
        </el-col>
      </el-row>
    </template>
    
    <app-custom-alert
      :visible.sync="confirmationAlertVisible"
      closeable
      type="delete"
      :confirm-text="i18n('common.yes')"
      :title="i18n('teamRegistrationSite.form.step3.confirm')"
      :on-confirm-fn="() => $emit('delete')"
    />
  </el-form>
</template>

<script>
import _ from 'lodash'

import AppFormSubtitle from '@/components/FormSubtitle.vue'

import { i18n } from '@shared/i18n'
import { FormSchema } from '@shared/form/form-schema'

import { ClubContactPersonModel } from '@/models/club-contact-person-model'

const { fields } = ClubContactPersonModel

const formSchema = new FormSchema([
  fields.firstName,
  fields.lastName,
  fields.position,
  fields.email,
  fields.phone,
])

export default {
  name: 'appContactPersonForm',
  props: {
    value: Object,
    index: Number,
    gutter: Number,
  },
  data() {
    return {
      i18n,
      fields,
      rules: formSchema.rules(),
      model: {},
      isCollapsed: false,
      confirmationAlertVisible: false,
    }
  },
  async mounted() {
    await this.$nextTick()
    this.validateAndCollapse(true)
  },
  methods: {
    async validateAndCollapse(clearValidation = false) {
      try {
        await this.$refs.form.validate()
        this.isCollapsed = true
      } catch (e) {
        // 
      } finally {
        if (clearValidation) this.$refs.form.clearValidate()
      }
    },
    uncollapse() {
      this.isCollapsed = false
    },
    showConfirmationAlert() {
      this.confirmationAlertVisible = true
    },
  },
  watch: {
    value: {
      handler(val, oldVal) {
        if (!val) return
        if (_.isEqual(val, oldVal)) return
        this.model = val
      },
      deep: true,
      immediate: true,
    },
    model: {
      handler(val, oldVal) {
        if (_.isEqual(val, oldVal)) return
        this.$emit('input', val)
      },
      deep: true,
      immediate: true,
    },
  },
  components: {
    AppFormSubtitle,
  },
}
</script>

<style lang="scss" scoped>
.contact-person-form {
  &__save {
    display: flex;
    height: 60px;

    @include tablet {
      display: flex;
      height: 94px;
      align-items: center;
      justify-content: flex-end;
    }

    &__button {
      flex-grow: 1;
      margin: 8px 0 0;

      @include tablet {
        flex-grow: initial;
        margin: 0;
      }
    }
  }

  &__header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 8px;
    align-items: center;

    @include tablet {
      padding: 16px 8px;
      flex-wrap: initial;
    }
  }

  &__subtitle {
    margin: 2px 0 0;
    align-self: flex-start;
  }

  &__overview {
    text-align: left;
    font-size: 14px;
    font-weight: normal;
    order: 4;
    flex: 1 1 100%;
    margin-top: 8px;

    @include tablet {
      margin-top: 0;
      flex: initial;
      flex-grow: 1;
      margin: 0 24px;
      order: initial;
    }
  }

  &__controls {
    display: flex;
    align-items: center;
  }

  &__edit {
    cursor: pointer;
    display: flex;
    align-items: center;

    &__icon {
      color: #2acccc;
      margin-right: 8px;
      font-size: 20px;

      @include tablet {
        font-size: 14px;
      }
    }
  }

  &__delete {
    cursor: pointer;
    font-size: 20px;

    @include tablet {
      font-size: 14px;
    }
  }
}
</style>
